import { BASE_URL } from "../../global";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import { certQuery } from "./certQuery";

export const fetchActiveRoles = async () => {
  try {
    const url = `${BASE_URL}settings?_where=(keyName,like,activeRole~)~and(status,ne,-1)`;
    const res = await axiosWithToken.get(url);
    const validRoles = (res?.data || []).filter(role => {
      // Exclude roles with value === "false" (string) or falsy values
      return role.value && role.value.toLowerCase() !== "false";
    });
    return validRoles || [];
  } catch (err) {
    console.error("Error fetching active roles:", err);
    return [];
  }
};

export const fetchCertificateDetails = async (params) => {
  try {
    // Check if params.id contains comma-separated values
    const ids = params.id ? params.id.split(",").map((id) => id.trim()) : [];
    if (ids.length === 0) {
      return [];
    }
    // Convert array of IDs to comma-separated string for SQL IN clause
    const idList = ids
      ?.map((id) => parseInt(id))
      ?.filter((id) => !isNaN(id))
      ?.join(",");

    if (!idList) {
      return [];
    }

   // Fetch active roles directly inside the function
    const activeRoles = await fetchActiveRoles();
    const results = await fetchQuery(`${certQuery} WHERE certificate.id IN (${idList}) ORDER BY certificate.id;`);

    // Group flat SQL rows by certificate.id
    const certMap = new Map();

    for (const item of results || []) {
      const certId = item.id;

      if (!certMap.has(certId)) {
        
        // combine group names 
        const allGroupIds = results.filter(r => r.id === certId && r.inwardTests_groupId).map(r => r.inwardTests_groupId).join(",");
        // combine material names
        const allMaterialIds = results.filter(r => r.id === certId && r.inwardTests_materialId).map(r => r.inwardTests_materialId).join(",");
        // Fetch group names for each certificate
        const groupNames = await fetchGroupNames(allGroupIds);
        // Fetch material names for each certificate
        const materialNames = await fetchMaterialNames(allMaterialIds);
        // Fetch inwardTests for each certificate
        const { rows: inwardTestsList, testHeaders: inwardTestsHeaders } = await fetchInwardTests(item.inward_id);
        const witnessString = await fetchWitnessString(certId);

        // Build certificate_footer once per certificate
        const certificate_footer = {};
        activeRoles.forEach((role) => {
          const roleKey = role.keyName.toLowerCase(); // e.g., activeRole1
          const label = role.value; // e.g., Tested By

          if (roleKey.includes("1")) {
            certificate_footer.testedBy = {
              label,
              userName: item.testedBy_userName || "",
              hrDesignation: item.testedBy_hrDesignation || "",
              usersign: item.testedBy_userSign || ""
            };
          } else if (roleKey.includes("2")) {
            certificate_footer.preparedBy = {
              label,
              userName: item.preparedBy_userName || "",
              hrDesignation: item.preparedBy_hrDesignation || "",
              usersign: item.preparedBy_userSign || ""
            };
          } else if (roleKey.includes("3")) {
            certificate_footer.reviewedBy = {
              label,
              userName: item.reviewedBy_userName || "",
              hrDesignation: item.reviewedBy_hrDesignation || "",
              usersign: item.reviewedBy_userSign || ""
            };
          } else if (roleKey.includes("4")) {
            certificate_footer.authorizedBy = {
              label,
              userName: item.authorizedBy_userName || "",
              hrDesignation: item.authorizedBy_hrDesignation || "",
              usersign: item.authorizedBy_userSign || ""
            };
          }
        });

        // Store certificate-level fields once
        certMap.set(certId, {
          id: item.id,
          certificate_reportNumber: item.certificate_reportNumber,
          certificate_ulrNumber: item.certificate_ulrNumber,
          certificate_rejectedBy: item.certificate_rejectedBy,
          certificate_preparedBy: item.certificate_preparedBy,
          certificate_preparedDate: item.certificate_preparedDate,
          certificate_reviewedBy: item.certificate_reviewedBy,
          certificate_reviewedDate: item.certificate_reviewedDate,
          certificate_authorizedBy: item.certificate_authorizedBy,
          certificate_authorizedDate: item.certificate_authorizedDate,
          certificate_status: item.certificate_status,
          // user sign fields
          testedBy_userName: item.testedBy_userName,
          testedBy_hrDesignation: item.testedBy_hrDesignation,
          testedBy_userSign: item.testedBy_userSign,
          preparedBy_userName: item.preparedBy_userName,
          preparedBy_hrDesignation: item.preparedBy_hrDesignation,
          preparedBy_userSign: item.preparedBy_userSign,
          reviewedBy_userName: item.reviewedBy_userName,
          reviewedBy_hrDesignation: item.reviewedBy_hrDesignation,
          reviewedBy_userSign: item.reviewedBy_userSign,
          authorizedBy_userName: item.authorizedBy_userName,
          authorizedBy_hrDesignation: item.authorizedBy_hrDesignation,
          authorizedBy_userSign: item.authorizedBy_userSign,
          // inward fields
          inward_inwardDate: item.inward_inwardDate,
          inward_address: item.inward_address,
          inward_labReferenceNo: item.inward_labReferenceNo,
          inward_principleClient: item.inward_principleClient,
          inward_sampleDetails: item.inward_sampleDetails,
          inward_disciplineId: item.inward_disciplineId,
          inward_locations: item.inward_locations,
          // job fields
          jobRegister_clientName: item.jobRegister_clientName,
          jobRegister_projectName: item.jobRegister_projectName,
          jobRegister_reference: item.jobRegister_reference,
          // material / discipline
          discipline_groupName: groupNames,
          materialScope_MaterialName: materialNames,
          inwardTests_quantity: item.inwardTests_quantity,
          // footer
          certificate_footer,
          // observation sheets array — filled below
          observationSheets: [],
          inwardTestsList,
          inwardTestsHeaders,
          witnessString
        });
      }

      // Skip if no observation sheet on this row
      if (!item.observationSheet_id) continue;

      // Parse configuration
      const obsConfig =
        typeof item.observationSheet_configuration === "string"
          ? JSON.parse(item.observationSheet_configuration || "{}")
          : item.observationSheet_configuration;

      // Fetch static tables and reading rows for this observation sheet
      const [staticTables, readingRows] = await Promise.all([
        fetchCertificateStaticTables(
          item.materialTestings_materialId,
          item.materialTestings_id,
          item.deletedTableIds || ""
        ),
        fetchStaticReadingRows(item.observationSheet_id),
      ]);

      // Push one observation sheet entry with ALL inwardTests fields
      certMap.get(certId).observationSheets.push({
        observationSheet_id: item.observationSheet_id,
        observationSheet_inwardTestId: item.observationSheet_inwardTestId,
        observationSheet_testId: item.observationSheet_testId,
        observationSheet_testNumber: item.observationSheet_testNumber,
        observationSheet_boreHoleNo: item.observationSheet_boreHoleNo,
        observationSheet_startTestingDate: item.observationSheet_startTestingDate,
        observationSheet_endTestingDate: item.observationSheet_endTestingDate,
        observationSheet_reportNumber: item.certificate_reportNumber,
        observationSheet_ulrNumber: item.observationSheet_ulrNumber,
        observationSheet_totalReading: item.observationSheet_totalReading,
        observationSheet_configuration: obsConfig,
        observationSheet_testedBy: item.observationSheet_testedBy,
        observationSheet_approvedBy: item.observationSheet_approvedBy,
        observationSheet_fileAttachment: item.observationSheet_fileAttachment,
        observationSheet_complianceStatus: item.observationSheet_complianceStatus,
        observationSheet_additionalTestDetails: item.observationSheet_additionalTestDetails ? JSON.parse(item.observationSheet_additionalTestDetails || "{}") : {},
        observationSheet_requestApproval: item.observationSheet_requestApproval,
        observationSheet_status: item.observationSheet_status,
        // inward fields
        inward_principleClient: item.inward_principleClient || "",
        // job fields
        jobRegister_clientName: item.jobRegister_clientName || "",
        jobRegister_projectName: item.jobRegister_projectName || "",
        jobRegister_jobNumber: item.jobRegister_jobNumber || "",
        // material testings
        materialTestings_materialId: item.materialTestings_materialId,
        materialTestings_id: item.materialTestings_id,
        materialTestings_testName: item.materialTestings_testName,
        materialTestings_testMethod: item.materialTestings_testMethod,
        // ── all inwardTests fields ──────────────────────────────────
        inwardTests_id: item.inwardTests_id,
        inwardTests_boreHoleNo: item.inwardTests_boreHoleNo,
        inwardTests_lengthOfRock: item.inwardTests_lengthOfRock,
        inwardTests_locationChainageNo: item.inwardTests_locationChainageNo,
        inwardTests_fromDepth: item.inwardTests_fromDepth,
        inwardTests_toDepth: item.inwardTests_toDepth,
        inwardTests_sampleType: item.inwardTests_sampleType,
        inwardTests_sampleCondition: item.inwardTests_sampleCondition,
        inwardTests_SamplingDetails: item.inwardTests_SamplingDetails,
        inwardTests_lotNumber: item.inwardTests_lotNumber,
        inwardTests_quantity: item.inwardTests_quantity,
          // ADD tested by user 
        testedBy_userName: item.testedBy_userName || "",
        testedBy_hrDesignation: item.testedBy_hrDesignation || "",
        testedBy_userSign: item.testedBy_userSign || "",
        // ────────────────────────────────────────────────────────────
        tableDisplayStatus: item.tableDisplayStatus,
        tableDivide: item.tableDivide,
        staticTables,
        readingRows,
      });
    }

    for (const cert of certMap.values()) {
      const starts = cert.observationSheets.map(o => o.observationSheet_startTestingDate).filter(Boolean);
      const ends = cert.observationSheets.map(o => o.observationSheet_endTestingDate).filter(Boolean);
      cert.observationSheet_startTestingDate = starts.length ? new Date(Math.min(...starts.map(d => new Date(d)))).toISOString() : null;
      cert.observationSheet_endTestingDate = ends.length ? new Date(Math.max(...ends.map(d => new Date(d)))).toISOString() : null;
      cert.inwardTests_quantity = [...new Set(cert.observationSheets.map(o => o.inwardTests_quantity).filter(Boolean))].join(", ");
      cert.inwardTests_sampleCondition = [...new Set(cert.observationSheets.map(o => o.inwardTests_sampleCondition).filter(Boolean))].join(", ");
      cert.inwardTests_SamplingDetails = [...new Set(cert.observationSheets.map(o => o.inwardTests_SamplingDetails).filter(Boolean))].join(", ");
    }

    return Array.from(certMap.values());
  } catch (error) {
    console.error("Error fetching certificates:", error);
    return [];
  }
};

export const fetchSettings = async () => {
  try {
    const { data = [] } = await axiosWithToken.get(`${BASE_URL}settings?_where=(status,eq,1)`);
    const roleSettings = {}, settings = {};
    data.forEach(({ keyName, value }) => {
      if (!keyName) return;
      keyName.startsWith('activeRole')
        ? roleSettings[keyName.split('activeRole')[1]] = { name: value, id: keyName.split('activeRole')[1] }
        : settings[keyName] = value;
    });
    return { roleSettings, settings, allSettings: data, };
  } catch (e) {
    console.error('Error fetching settings:', e);
  }
};

// Return promise with tables instead of setState
export function fetchCertificateStaticTables(materialId, testId, deletedTableIds = []) {
  deletedTableIds = deletedTableIds?.split?.(",")?.map((id) => Number(id)) || [];

  return axiosWithToken
    .get(BASE_URL + `certificateTemplates?_where=(materialId,eq,${materialId})~and(testId,eq,${testId})~and(status,eq,1)`)
    .then((res) => {
      let tables = res.data || [];
      if (deletedTableIds.length > 0) {
        tables = tables.filter((row) => !deletedTableIds.includes(row.id));
      }
      tables = tables.map((table) => ({
        ...table,
        defaultConfiguration: JSON.parse(table?.defaultConfiguration || null),
      }));
      return tables;
    })
    .catch((err) => console.log("error certificateStaticTables " + err));
}

export function fetchStaticReadingRows(certificateId) {
  return axiosWithToken
    .get(BASE_URL + `observationSheetReadings?_where=(observationsheetId,eq,${certificateId})`)
    .then((res) => res.data || [])
    .catch((err) => console.log("datasheetStaticReadings certificateStaticTables " + err));
}

// fetch inwardtests table from inwardID
export async function fetchInwardTests(inwardId) {
  try {
    // 1. Fetch all inwardTests rows for this inward
    const rows = await axiosWithToken
      .post(BASE_URL + `dynamic`, {
        query: `SELECT * FROM inwardTests WHERE inwardId = ${inwardId} AND status != -1 ORDER BY id`,
      })
      .then((res) => res.data || [])
      .catch((err) => { console.log("fetchInwardTests " + err); return []; });

    // 2. Collect all unique testIds across all rows
    const allTestIds = [...new Set(
      rows.flatMap((row) =>
        String(row.testId || "").split(",").map((id) => id.trim()).filter(Boolean)
      )
    )];

    if (allTestIds.length === 0) return { rows: [], testHeaders: [] };

    // 3. Fetch test names for all unique testIds in one query
    const testNamesList = await axiosWithToken
      .post(BASE_URL + `dynamic`, {
        query: `SELECT id, testName, testMethod FROM materialTestings WHERE id IN (${allTestIds.join(",")}) AND status != -1`,
      })
      .then((res) => res.data || [])
      .catch(() => []);

    // Build map: testId -> testName
    const testNameMap = {};
    const testMethodMap = {};
    testNamesList.forEach((t) => {
      testNameMap[String(t.id)] = t.testName;
      testMethodMap[String(t.id)] = t.testMethod || "";
    });

    // 4. Fetch observation templates — id is the tableId referenced in observationSheetReadings
    const observationTemplates = await axiosWithToken
      .post(BASE_URL + `dynamic`, {
        query: `SELECT id, testId, c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, c11, c12, c13, c14, c15, c16, c17, c18, c19, c20, c21, c22, c23, c24, c25, c26, c27, c28, c29, c30, defaultConfiguration, summaryColumn FROM observationTemplates WHERE testId IN (${allTestIds.join(",")}) AND status = 1`,
      })
      .then((res) => res.data || [])
      .catch(() => []);

    // Build map: testId -> { columns, summaryTableId (= template.id), summaryColumnKeys }
    const testColumnMap = {};
    observationTemplates.forEach((template) => {
      const testId = String(template.testId);

      const summaryColumnStr = template.summaryColumn || "";
      const summaryColumnKeys = summaryColumnStr.split(",").map(k => k.trim()).filter(Boolean);

      // only build columns from summaryColumn keys — no c1 default fallback
      const columns = summaryColumnKeys.map(columnKey => ({
        key: columnKey,
        name: template[columnKey] || columnKey.toUpperCase()
      }));

      // template.id IS the tableId used in observationSheetReadings
      if (!testColumnMap[testId]) {
        testColumnMap[testId] = {
          columns,
          summaryTableId: summaryColumnKeys.length > 0 ? String(template.id) : null,
          summaryColumnKeys
        };
      } else if (summaryColumnKeys.length > 0) {
        // if multiple templates for same testId, prefer the one with summaryColumn defined
        testColumnMap[testId] = {
          columns,
          summaryTableId: String(template.id),
          summaryColumnKeys
        };
      }
    });

    // 5. Fetch observationSheets filtered to THIS inward's inwardTests only
    const inwardTestRowIds = rows.map((r) => r.id);
    const obsSheetsList = await axiosWithToken
      .post(BASE_URL + `dynamic`, {
        query: `SELECT id, testId, inwardTestId FROM observationSheet WHERE inwardTestId IN (${inwardTestRowIds.join(",")}) AND status != -1 AND inwardId = ${inwardId} ORDER BY inwardTestId, id`,
      })
      .then((res) => res.data || [])
      .catch(() => []);

    // Build map: testId -> [observationsheetId, ...]  (kept for unit lookup)
    const testIdToObsIds = {};
    // map inwardTestId+testId -> obsSheet id
    // inwardTestId in DB can be comma-separated like "17,18,19" — split and map each
    const inwardTestAndTestIdToObsId = {};
    obsSheetsList.forEach((obs) => {
      const key = String(obs.testId);
      if (!testIdToObsIds[key]) testIdToObsIds[key] = [];
      testIdToObsIds[key].push(obs.id);

      // split inwardTestId in case it's comma-separated like "17,18,19"
      const inwardTestIds = String(obs.inwardTestId || "").split(",").map(s => s.trim()).filter(Boolean);
      inwardTestIds.forEach((itId) => {
        const mapKey = `${itId}_${obs.testId}`;
        inwardTestAndTestIdToObsId[mapKey] = obs.id;
      });
    });

    // 6. Fetch all readings for all those observationsheet IDs in one query
    const allObsIds = obsSheetsList.map((o) => o.id);
    const allReadings = allObsIds.length > 0
      ? await axiosWithToken
        .post(BASE_URL + `dynamic`, {
          query: `SELECT id, tableId, observationsheetId, c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, c11, c12, c13, c14, c15, c16, c17, c18, c19, c20, c21, c22, c23, c24, c25, c26, c27, c28, c29, c30 FROM observationSheetReadings WHERE observationsheetId IN (${allObsIds.join(",")}) ORDER BY observationsheetId, id`,
        })
        .then((res) => res.data || [])
        .catch(() => [])
      : [];

    // Build map: obsId+tableId -> data rows, obsId+tableId -> unit row
    // Also build obsId -> all data rows (for fallback tests without summaryColumn)
    const obsTableToReadings = {};
    const obsTableToUnitRow = {};
    const obsIdToAllReadings = {};
    allReadings.forEach((r) => {
      const obsKey = String(r.observationsheetId);
      const tableKey = `${obsKey}_${r.tableId}`;
      const raw = String(r.c1 || "");

      if (raw.includes("_rh_")) return;

      if (raw.includes("_unit_")) {
        obsTableToUnitRow[tableKey] = r;
        return;
      }

      if (!obsTableToReadings[tableKey]) obsTableToReadings[tableKey] = [];
      obsTableToReadings[tableKey].push(r);

      // also collect into obsId -> all rows for fallback
      if (!obsIdToAllReadings[obsKey]) obsIdToAllReadings[obsKey] = [];
      obsIdToAllReadings[obsKey].push(r);
    });

    // 7. Build unit map: testId -> columnKey -> unit string
    const testIdToColumnUnits = {};
    allTestIds.forEach((testId) => {
      const obsIds = testIdToObsIds[testId] || [];
      const { summaryColumnKeys = [], summaryTableId, columns = [] } = testColumnMap[testId] || {};
      testIdToColumnUnits[testId] = {};
      const keysToCheck = summaryColumnKeys.length > 0 ? summaryColumnKeys : columns.map(c => c.key);
      keysToCheck.forEach((columnKey) => {
        for (const obsId of obsIds) {
          let unitRow = null;
          if (summaryTableId) {
            unitRow = obsTableToUnitRow[`${obsId}_${summaryTableId}`];
          } else {
            const matchingKeys = Object.keys(obsTableToUnitRow).filter(k => k.startsWith(obsId + "_"));
            if (matchingKeys.length > 0) unitRow = obsTableToUnitRow[matchingKeys[0]];
          }
          if (unitRow?.[columnKey]) {
            const raw = String(unitRow[columnKey]);
            if (raw.startsWith("_unit_")) {
              testIdToColumnUnits[testId][columnKey] = raw.replace("_unit_", "").trim();
            } else {
              const hashIdx = raw.indexOf("#");
              if (hashIdx !== -1) {
                testIdToColumnUnits[testId][columnKey] = raw.substring(hashIdx + 1).trim();
              }
            }
            break;
          }
        }
      });
    });

    // 8. Build testHeaders NOW (after units are ready)
    const testHeaders = [];
    allTestIds.forEach((id) => {
      const { columns = [] } = testColumnMap[id] || {};
      if (columns.length === 0) return;
      const testName = testNameMap[id] || `Test ${id}`;
      columns.forEach((col, colIndex) => {
        const unit = testIdToColumnUnits[id]?.[col.key] || "";
        testHeaders.push({
          testId: id,
          testName: testName,
          testMethod: testMethodMap[id] || "",
          columnKey: col.key,
          columnName: col.name,
          columnUnit: unit,
          isFirstColumn: colIndex === 0,
          columnSpan: columns.length
        });
      });
    });

    // OLD fallback: sequential values per testId+columnKey for tests without summaryColumn
    const testIdToColumnValues = {};
    const testIdColumnCounter = {};
    allTestIds.forEach((testId) => {
      const { columns = [], summaryTableId } = testColumnMap[testId] || {};
      if (summaryTableId) return; // skip — handled by exact lookup in step 9
      if (columns.length === 0) return;
      const obsIds = testIdToObsIds[testId] || [];
      testIdToColumnValues[testId] = {};
      testIdColumnCounter[testId] = {};
      columns.forEach(({ key: columnKey }) => {
        testIdColumnCounter[testId][columnKey] = 0;
        const columnValues = [];
        obsIds.forEach((obsId) => {
          // use all readings for this obsId (all tables) for fallback
          const readings = obsIdToAllReadings[String(obsId)] || [];
          readings.forEach((r) => {
            const raw = String(r[columnKey] || "");
            const cleaned = (raw.includes("#") ? raw.split("#")[0] : raw.replace(/\$/g, " ")).trim();
            columnValues.push(cleaned);
          });
        });
        testIdToColumnValues[testId][columnKey] = columnValues;
      });
    });

    // per obsId+tableKey reading counter for multi-inwardTest obsSheets
    const obsIdReadingCounter = {};

    // 9. Build inwardTestId+testId -> columnKey -> reading value
    const inwardTestReadingMap = {};

    rows.forEach((row) => {
      const rowTestIds = String(row.testId || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      rowTestIds.forEach((testId) => {
        const mapKey = `${row.id}_${testId}`;
        const obsId = inwardTestAndTestIdToObsId[mapKey];
        const { summaryColumnKeys = [], summaryTableId, columns = [] } = testColumnMap[testId] || {};

        if (!inwardTestReadingMap[row.id]) inwardTestReadingMap[row.id] = {};
        inwardTestReadingMap[row.id][testId] = {};

        if (summaryTableId) {
          // exact lookup: per inwardTest row via summaryTableId
          if (!obsId || summaryColumnKeys.length === 0) {
            columns.forEach(({ key }) => {
              inwardTestReadingMap[row.id][testId][key] = "";
            });
            return;
          }
          const tableKey = `${obsId}_${summaryTableId}`;
          const readings = obsTableToReadings[tableKey] || [];

          // distribute readings sequentially — one reading row per inwardTest row
          if (!obsIdReadingCounter[tableKey]) obsIdReadingCounter[tableKey] = 0;
          const idx = obsIdReadingCounter[tableKey];
          const readingRow = readings[idx] || null;
          obsIdReadingCounter[tableKey] = idx + 1;

          summaryColumnKeys.forEach((columnKey) => {
            if (!readingRow) {
              inwardTestReadingMap[row.id][testId][columnKey] = "";
              return;
            }
            const raw = String(readingRow[columnKey] || "");
            inwardTestReadingMap[row.id][testId][columnKey] =
              (raw.startsWith("$") ? raw.slice(1) : raw).split("#")[0].trim();
          });
        } else {
          // OLD sequential fallback for tests without summaryColumn
          if (!testIdToColumnValues[testId]) return;
          const cols = testColumnMap[testId]?.columns || [];
          cols.forEach(({ key: columnKey }) => {
            const columnValues = testIdToColumnValues[testId][columnKey] || [];
            const idx = testIdColumnCounter[testId][columnKey] ?? 0;
            inwardTestReadingMap[row.id][testId][columnKey] = columnValues[idx] ?? "";
            testIdColumnCounter[testId][columnKey] = idx + 1;
          });
        }
      });
    });

    // 10. Attach readingByTestId to each row
    const rowsWithTestIds = rows.map((row) => {
      const rowTestIds = String(row.testId || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      return {
        ...row,
        rowTestIdSet: new Set(rowTestIds),
        readingByTestId: inwardTestReadingMap[row.id] || {},
      };
    });

    return { rows: rowsWithTestIds, testHeaders };
  } catch (err) {
    console.log("fetchInwardTests error: " + err);
    return { rows: [], testHeaders: [] };
  }
}

// Add this helper function
export async function fetchGroupNames(ids = "") {
  const unique = [...new Set(String(ids).split(",").map(s => s.trim()).filter(Boolean))];
  if (!unique.length) return "";
  const rows = await axiosWithToken
    .post(BASE_URL + `dynamic`, {
      query: `SELECT id, groupName FROM discipline WHERE id IN (${unique.join(",")}) AND status != -1`,
    })
    .then((r) => r.data || [])
    .catch((err) => console.log(err));
  return unique
    .map((id) => rows.find((r) => String(r.id) === id)?.groupName)
    .filter(Boolean)
    .join(", ");
}

export async function fetchMaterialNames(ids = "") {
  const unique = [...new Set(String(ids).split(",").map(s => s.trim()).filter(Boolean))];
  if (!unique.length) return "";
  const rows = await axiosWithToken
    .post(BASE_URL + `dynamic`, {
      query: `SELECT id, MaterialName FROM materialScope WHERE id IN (${unique.join(",")}) AND status != -1`,
    })
    .then((r) => r.data || [])
    .catch((err) => console.log(err));
  return unique
    .map((id) => rows.find((r) => String(r.id) === id)?.MaterialName)
    .filter(Boolean)
    .join(", ");
}

export const fetchWitnessString = async (certificateId) => {
  if (!certificateId) return "";
  try {
    const result = await fetchQuery(`SELECT name, companyName FROM ObservationWitnessHistory WHERE certificateId = ${certificateId} 
       ORDER BY id ASC`);
    if (!result?.length) return "";
    return result
      .filter((w) => w.name)
      .map((w) => (w.companyName ? `${w.name} (${w.companyName})` : w.name))
      .join(", ");
  } catch (error) {
    console.log(error);
    return "";
  }
};